/**
 * The custom orders list: which orders a search and the filter chips leave. Pure, so it is tested
 * (custom-filters.test.ts). Chips in one group widen the list (BOX NOW or Meet-up); chips in different
 * groups narrow it (BOX NOW and still owes money). The search works inside the chosen tab.
 */

export type ListOrder = {
  order_number: string
  handle: string
  parcel_status: string | null
  items: Array<{ title: string; size: string | null }>
  delivery: "box_now" | "home" | "meetup" | "elta" | "unknown"
  hq_delivery?: "box_now" | "home" | "meetup" | "elta" | null
  sent_to_larry?: boolean
  contacted_at?: string | null
  left?: number | null
  settled?: boolean
  /** The row colour: a completed order counts as paid (the sheet never records the balance taken at hand-over). */
  status?: "active" | "pending" | "completed" | "cancelled"
}

export const FILTER_GROUPS = {
  delivery: ["box_now", "meetup", "home", "elta"],
  money: ["owes", "paid"],
  larry: ["with_larry", "not_with_larry"],
  contact: ["contacted", "not_contacted"],
} as const

export type FilterGroup = keyof typeof FILTER_GROUPS
export type CustomFilter = (typeof FILTER_GROUPS)[FilterGroup][number]
export const ALL_FILTERS = Object.values(FILTER_GROUPS).flat() as CustomFilter[]

/** The delivery HQ recorded (what was agreed with the customer), else the sheet's. */
export const deliveryOf = (order: ListOrder) => order.hq_delivery ?? order.delivery

/** Still has money to pay: a price, not settled, and something left. */
const finished = (order: ListOrder) => order.status === "completed" || order.status === "cancelled"
export const owes = (order: ListOrder) => !finished(order) && !order.settled && order.left != null && order.left > 0

function passes(order: ListOrder, filter: CustomFilter): boolean {
  switch (filter) {
    case "box_now":
    case "meetup":
    case "home":
    case "elta":
      return deliveryOf(order) === filter
    case "owes":
      return owes(order)
    case "paid":
      return order.status === "completed" || !!order.settled || order.left === 0
    case "with_larry":
      return !!order.sent_to_larry
    case "not_with_larry":
      return !order.sent_to_larry
    case "contacted":
      return !!order.contacted_at
    case "not_contacted":
      return !order.contacted_at
  }
}

export function matchesFilters(order: ListOrder, filters: CustomFilter[]): boolean {
  for (const group of Object.values(FILTER_GROUPS) as ReadonlyArray<readonly CustomFilter[]>) {
    const chosen = filters.filter((f) => group.includes(f))
    if (chosen.length && !chosen.some((f) => passes(order, f))) return false
  }
  return true
}

/** Case- and accent-blind, so «αλέξης» finds «Αλεξης». */
export const fold = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/ς/g, "σ")

/** «#1668», «C-1668», «c1668» are the order number; «@name» is the handle; anything else is searched as typed. */
export function searchTerm(raw: string): string {
  const term = fold(raw.trim())
  if (/^(#|c-?)\d/.test(term)) return term.replace(/^(#|c-?)/, "")
  return term.replace(/^@/, "")
}

export function matchesSearch(order: ListOrder, raw: string): boolean {
  const term = searchTerm(raw)
  if (!term) return true
  return [order.order_number, order.handle.replace(/^@/, ""), order.parcel_status ?? "", ...order.items.map((item) => `${item.title} ${item.size ?? ""}`)].some((field) => fold(field).includes(term))
}

/** Filters from the address (?f=box_now,owes), keeping only known ones. */
export const parseFilters = (value: string | null): CustomFilter[] => (value ?? "").split(",").filter((f): f is CustomFilter => (ALL_FILTERS as string[]).includes(f))
